import { useCallback, useEffect, useState } from 'react';
import {
  Accordion,
  AccordionHeader,
  AccordionItem,
  AccordionPanel,
  Badge,
  Text,
  mergeClasses,
  type AccordionToggleEventHandler,
} from '@coreai/fluentui-react';
import type { OperationDefinition } from '@/types/operations';
import { ConnectorBrandIcon } from './ConnectorBrandIcon';
import { ConnectorFavoriteButton } from './ConnectorFavoriteButton';
import { ConnectorOperationsList } from './ConnectorOperationsList';
import { HighlightText } from './HighlightText';
import { useConnectorSectionStyles } from './ConnectorSection.styles';

type ConnectorSectionProps = {
  connectorName: string;
  operations: OperationDefinition[];
  onOperationSelect: (operation: OperationDefinition, event?: React.MouseEvent) => void;
  defaultExpanded?: boolean;
  /** Controlled expanded state. When provided, overrides internal state. */
  isExpanded?: boolean;
  onExpandedChange?: (expanded: boolean) => void;
  /** Disable margin when rendered inside a virtualized list. */
  noMargin?: boolean;
  initialLimit?: number;
  showAll?: boolean;
  onShowAllChange?: (showAll: boolean) => void;
  /** Reports the displayed count after internal filtering (for height calc). */
  onDisplayedCountChange?: (count: number, isSearching: boolean) => void;
  /** Outer search query for inline highlighting. */
  searchQuery?: string;
  /** Whether to show the "Built-in" badge in the header. */
  isBuiltIn?: boolean;
  /** Roving-tabindex pattern in virtualized lists. */
  headerTabIndex?: number;
  /** Index attribute used for keyboard focus targeting in virtualized lists. */
  headerIndex?: number;
  /**
   * Whether the section can be collapsed. Defaults to `true`. When `false`
   * (e.g. the search drill-in view) the chevron is hidden and the header
   * loses its clickable affordance — the panel is always shown.
   */
  isCollapsible?: boolean;
};

export const ConnectorSection = ({
  connectorName,
  operations,
  onOperationSelect,
  defaultExpanded = false,
  isExpanded: controlledExpanded,
  onExpandedChange,
  noMargin = false,
  initialLimit,
  showAll = false,
  onShowAllChange,
  onDisplayedCountChange,
  searchQuery = '',
  isBuiltIn = false,
  headerTabIndex,
  headerIndex,
  isCollapsible = true,
}: ConnectorSectionProps) => {
  const styles = useConnectorSectionStyles();
  const [internalExpanded, setInternalExpanded] = useState(defaultExpanded);
  // When non-collapsible, force expanded regardless of incoming props
  // so the drill-in view always shows operations.
  const isExpanded = !isCollapsible ? true : (controlledExpanded ?? internalExpanded);
  const openItems = isExpanded ? [connectorName] : [];

  const handleToggle: AccordionToggleEventHandler = useCallback(
    (_e, data) => {
      if (!isCollapsible) return;
      const next = data.openItems.includes(connectorName);
      setInternalExpanded(next);
      onExpandedChange?.(next);
    },
    [connectorName, isCollapsible, onExpandedChange],
  );

  // Header chrome is driven by the first operation's metadata.
  const firstOp = operations[0];
  const brandColor = firstOp?.brandColor || '#0078D4';
  const iconUri = firstOp?.icon;
  const description = firstOp?.connectorDescription;
  const favoriteId = firstOp?.connectorId;

  // Notify parent of the operation count when the section is collapsed —
  // ConnectorOperationsList only fires while mounted (i.e. expanded).
  useEffect(() => {
    if (!isExpanded) {
      onDisplayedCountChange?.(operations.length, false);
    }
  }, [isExpanded, operations.length, onDisplayedCountChange]);

  return (
    <Accordion
      collapsible
      openItems={openItems}
      onToggle={handleToggle}
      className={mergeClasses(styles.container, noMargin && styles.containerNoMargin)}
    >
      <AccordionItem value={connectorName}>
        <AccordionHeader
          expandIconPosition="start"
          icon={<ConnectorBrandIcon brandColor={brandColor} iconUri={iconUri} />}
          // Hide the chevron when the section can't be collapsed —
          // showing it implies an interaction that does nothing.
          {...(!isCollapsible && { expandIcon: null })}
          className={mergeClasses(
            styles.header,
            isExpanded && styles.headerExpanded,
            !isCollapsible && styles.headerStatic,
          )}
          button={{
            'data-connector-header': true,
            'data-connector-index': headerIndex,
            tabIndex: headerTabIndex ?? 0,
            'aria-label': `${connectorName}, ${operations.length} operations`,
          } as Record<string, unknown>}
        >
          <div className={styles.connectorInfo}>
            <HighlightText
              text={connectorName}
              query={searchQuery}
              className={styles.connectorName}
            />
            {description && (
              <Text className={styles.connectorDescription}>{description}</Text>
            )}
          </div>
          <div className={styles.headerControls}>
            {isBuiltIn && (
              <Badge className={styles.builtInBadge} appearance="tint" color="brand" size="small">
                Built-in
              </Badge>
            )}
            {favoriteId && (
              <ConnectorFavoriteButton connectorId={favoriteId} connectorName={connectorName} />
            )}
            <Badge className={styles.badge} appearance="tint" color="informative" size="small">
              {operations.length}
            </Badge>
          </div>
        </AccordionHeader>
        <AccordionPanel className={styles.panel}>
          {isExpanded && (
            <ConnectorOperationsList
              operations={operations}
              onOperationSelect={onOperationSelect}
              brandColor={brandColor}
              initialLimit={initialLimit}
              showAll={showAll}
              {...(onShowAllChange && { onShowAllChange })}
              isExpanded={isExpanded}
              {...(onDisplayedCountChange && { onDisplayedCountChange })}
              outerSearchQuery={searchQuery}
              highlightInternalQuery
            />
          )}
        </AccordionPanel>
      </AccordionItem>
    </Accordion>
  );
};

export default ConnectorSection;
